import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Phone } from "lucide-react";
import Reveal from "@/components/Reveal";
import StoreMap from "@/components/StoreMap";
import { LOCATIONS, TYPE_COLOR } from "@/data/storeLocations";

const IMG = "https://customer-assets-7cd3h4nn.emergentagent.net/job_konattu-interactive/artifacts/91yuny0a_nathalia-rosa-rWMIbqmOxrY-unsplash.webp";
const TOWNS = [...new Set(LOCATIONS.map((l) => l.town))];
const GROUP_ORDER = ["Home Appliances", "Furniture", "Hypermart", "Emilia"];
const GROUPS = GROUP_ORDER.map((type) => ({ type, items: LOCATIONS.filter((l) => l.type === type) })).filter((g) => g.items.length);

const BranchCard = ({ b }) => (
  <div className="border border-[#F7F2E9]/12 p-5 h-full hover:border-konattu-red/60 transition-colors duration-300" data-testid="branch-card">
    <div className="flex items-center justify-between mb-2">
      <h4 className="font-display font-semibold text-[15px] text-[#F7F2E9] tracking-tight">{b.name}</h4>
      <span className="font-mono text-[9px] tracking-[0.2em] text-[#F7F2E9]/40 whitespace-nowrap ml-2">{b.town}</span>
    </div>
    <p className="font-body text-xs text-[#F7F2E9]/55 leading-relaxed mb-3">{b.address}</p>
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {b.phones.map((p) => (
        <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 font-mono text-[11px] text-[#F7F2E9]/80 hover:text-konattu-red transition-colors">
          <Phone size={11} className="text-konattu-red" /> {p}
        </a>
      ))}
    </div>
  </div>
);

export const StoreNetwork = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-14%", "14%"]);

  return (
    <section id="network" className="bg-konattu-charcoal py-20 md:py-28 overflow-hidden" data-testid="network-section">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div>
          <Reveal><span className="font-mono text-xs tracking-[0.35em] text-konattu-yellow">/ STORE LOCATIONS</span></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 mb-6 font-display font-bold text-[#F7F2E9] tracking-tight leading-[0.95] text-4xl md:text-6xl">
              {LOCATIONS.length} stores across Kerala.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="font-body text-[#F7F2E9]/75 text-base leading-relaxed max-w-lg mb-8">
              From Kottayam to Thodupuzha, Konattu Group connects communities with reliable home appliances, furniture, hypermarkets and lifestyle products, always close to the people we serve.
            </p>
          </Reveal>
          <div className="flex flex-wrap gap-2" data-testid="network-towns">
            {TOWNS.map((t, i) => (
              <Reveal key={t} delay={0.02 * i}>
                <span className="inline-flex items-center gap-1.5 border border-[#F7F2E9]/20 text-[#F7F2E9]/85 px-3 py-1.5 text-xs font-body hover:border-konattu-red hover:text-konattu-red transition-colors duration-300">
                  <MapPin size={11} className="text-konattu-red" /> {t}
                </span>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.15}>
          <div className="relative overflow-hidden aspect-[5/4] w-full">
            <motion.img src={IMG} alt="Konattu retail showroom" style={{ y }} className="absolute inset-0 w-full h-[128%] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-konattu-maroon/85 via-konattu-charcoal/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-7 flex items-end justify-between">
              <div>
                <div className="font-display font-extrabold text-5xl md:text-6xl text-[#F7F2E9] leading-none">{LOCATIONS.length}</div>
                <div className="font-mono text-[11px] tracking-[0.3em] text-konattu-yellow mt-2">RETAIL OUTLETS</div>
              </div>
              <span className="font-mono text-[10px] tracking-[0.25em] text-[#F7F2E9]/70 text-right">KOTTAYAM<br />→ THODUPUZHA</span>
            </div>
          </div>
        </Reveal>
      </div>

      <div className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-14 md:mt-16">
        <Reveal>
          <div className="flex items-center gap-2 mb-5">
            <MapPin size={14} className="text-konattu-red" />
            <span className="font-mono text-xs tracking-[0.3em] text-[#F7F2E9]/70">FIND US ON THE MAP</span>
          </div>
          <StoreMap />
        </Reveal>

        <div className="mt-12 space-y-10" data-testid="branch-directory">
          {GROUPS.map((g) => (
            <div key={g.type}>
              <Reveal>
                <div className="flex items-center gap-3 mb-4">
                  <span className="h-3 w-3" style={{ background: TYPE_COLOR[g.type] }} />
                  <h3 className="font-display font-semibold text-lg text-[#F7F2E9] tracking-tight">{g.type}</h3>
                  <span className="font-mono text-[11px] text-[#F7F2E9]/40">{g.items.length} stores</span>
                  <span className="flex-1 h-px bg-[#F7F2E9]/12" />
                </div>
              </Reveal>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {g.items.map((b, i) => (
                  <Reveal key={`${b.name}-${b.town}-${i}`} delay={(i % 3) * 0.04}>
                    <BranchCard b={b} />
                  </Reveal>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StoreNetwork;
